import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_LIBRARY_SEARCH } from '../analytics/events';
import { useNavigate, useLocation } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Container,
  Box,
  Typography,
  Chip,
  Skeleton,
  IconButton,
  InputBase,
  Paper,
  Grid,
  useMediaQuery,
  useTheme,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  EventSeat,
  Close,
  MyLocation,
  EmojiEvents,
  Today,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { useCityContext } from '../context/CityContext';
import { AppBarContext, useAppBarConfig } from '../context/AppBarContext';
import api from '../utils/api';
import StudentBottomNav from '../components/StudentBottomNav';
import LibraryCard from '../components/LibraryCard';
import DesktopNav from '../components/DesktopNav';
import SearchableCityDropdown from '../components/SearchableCityDropdown';
import SearchableAreaDropdown from '../components/SearchableAreaDropdown';
import useGeolocation from '../hooks/useGeolocation';
import PageHeader from '../components/shared/PageHeader';
import BottomSheet from '../components/shared/BottomSheet';
import EmptyState from '../components/shared/EmptyState';
import LibraryFilterPills from '../components/LibraryFilterPills';

const MotionBox = motion(Box);

const AMENITIES_LIST = ['24/7 Access', 'Parking', 'Discussion Rooms', 'Cafeteria'];
const SHEET_AMENITIES = ['Discussion Rooms', 'Cafeteria'];

function LibrariesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const { track } = useAnalytics();
  const lastSearchRef = useRef('');
  const { detectedCity } = useCityContext();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Detect whether we are mounted inside the StudentPortal shell
  const appBarCtx = useContext(AppBarContext);
  const inStudentShell = appBarCtx != null;

  const { coordinates, error: locationError, loading: locationLoading, requestPermission, clearLocation } = useGeolocation();

  const [libraries, setLibraries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Pagination seed kept in a ref so "Load More" reuses the page-1 seed reliably
  // (a useCallback closure could otherwise capture a stale state value, which made
  // later pages slice a differently-ordered list and render duplicate libraries).
  const seedRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
  });

  const searchParams = new URLSearchParams(location.search);
  const initialTopRated = searchParams.get('topRated') === 'true';
  const initialNearMe = searchParams.get('nearMe') === 'true';
  const initialMinDiscount = searchParams.get('minDiscount') || '';
  const initialOneDayPass = searchParams.get('oneDayPass') === 'true';

  const [filters, setFilters] = useState({
    city: '',
    locality: '',
    includeNearby: true,
    minPrice: '',
    maxPrice: '',
    amenities: [],
    sortBy: 'name',
    useLocation: initialNearMe || !isDesktop,
    topRated: initialTopRated,
    minDiscount: initialMinDiscount,
    oneDayPass: initialOneDayPass,
    nearbyRadius: 10,
  });

  const [localities, setLocalities] = useState([]);
  const [localitiesLoading, setLocalitiesLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const q = debouncedSearch;
    if (q && q !== lastSearchRef.current) {
      lastSearchRef.current = q;
      track(EVT_LIBRARY_SEARCH, { query: q, city: filters.city || '' });
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch localities for the selected city; reset locality whenever city changes.
  useEffect(() => {
    // Always reset the selected locality when city changes.
    setFilters((prev) => ({ ...prev, locality: '' }));

    if (!filters.city) {
      setLocalities([]);
      return;
    }

    let cancelled = false;
    setLocalitiesLoading(true);

    api
      .get('/libraries/localities', { params: { city: filters.city } })
      .then((res) => {
        if (!cancelled) {
          setLocalities(res.data?.data?.localities || []);
        }
      })
      .catch(() => {
        if (!cancelled) setLocalities([]);
      })
      .finally(() => {
        if (!cancelled) setLocalitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.city]); // eslint-disable-line react-hooks/exhaustive-deps

  const fromStudentPortal =
    location.state?.from === 'student-portal' || document.referrer.includes('/student');

  const fetchLibraries = useCallback(
    async (page = 1, append = false) => {
      try {
        if (!append) setLoading(true);

        // Generate a fresh seed on page 1 (new search/filter); reuse the SAME seed on
        // Load More so every page is sliced from the same backend ordering. Read/write
        // the seed via a ref — the useCallback closure can capture a stale searchSeed.
        let activeSeed;
        if (!append || seedRef.current == null) {
          activeSeed = Math.floor(Math.random() * 2_000_000_000);
          seedRef.current = activeSeed;
        } else {
          activeSeed = seedRef.current;
        }

        const params = {
          search: debouncedSearch || undefined,
          city: filters.city || undefined,
          locality: filters.city && filters.locality && !debouncedSearch ? filters.locality : undefined,
          includeNearby: filters.city && filters.includeNearby ? 'true' : undefined,
          minPrice: filters.minPrice || undefined,
          maxPrice: filters.maxPrice || undefined,
          sortBy: filters.sortBy,
          page,
          limit: 20,
          topRated: filters.topRated ? 'true' : undefined,
          minDiscount: filters.minDiscount || undefined,
          oneDayPass: filters.oneDayPass ? 'true' : undefined,
          seed: activeSeed,
        };

        if (filters.useLocation && coordinates) {
          params.userLat = coordinates.latitude;
          params.userLng = coordinates.longitude;
          params.nearbyRadius = filters.nearbyRadius;
          params.sortBy = 'distance';
        }

        if (filters.amenities.length > 0) {
          params.amenities = filters.amenities;
        }

        const response = await api.get('/libraries', { params });
        const newLibraries = response.data.data.libraries;
        const paginationData = response.data.data.pagination;

        if (append) {
          // Dedupe by id when appending so any ordering overlap between pages can
          // never render the same library card twice.
          setLibraries((prev) => {
            const seen = new Set(prev.map((l) => l.id));
            return [...prev, ...newLibraries.filter((l) => !seen.has(l.id))];
          });
        } else {
          setLibraries(newLibraries);
        }
        setPagination(paginationData);
      } catch (err) {
        console.error('Error fetching libraries:', err);
        setSnackbar({ open: true, message: 'Failed to fetch libraries', severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, filters, coordinates]
  );

  useEffect(() => {
    fetchLibraries();
  }, [fetchLibraries]);

  useEffect(() => {
    if (filters.useLocation && !coordinates && !locationError) {
      requestPermission();
    }
  }, [filters.useLocation, coordinates, locationError, requestPermission]);

  const handleBack = () => {
    if (fromStudentPortal || isAuthenticated) {
      navigate('/student');
    } else {
      navigate(-1);
    }
  };

  // Always call this hook — it is a no-op when ctx is null (public flow)
  useAppBarConfig(
    { title: 'Find libraries', subtitle: `${libraries.length} ${libraries.length === 1 ? 'library' : 'libraries'} found`, backTo: handleBack },
    [libraries.length]
  );

  const handleNearMe = () => {
    if (coordinates) {
      setFilters({ ...filters, useLocation: false });
      clearLocation();
      setSnackbar({ open: true, message: 'Location filter removed', severity: 'info' });
    } else {
      requestPermission();
      setFilters({ ...filters, useLocation: true });
    }
  };

  useEffect(() => {
    if (locationError) {
      setSnackbar({ open: true, message: locationError.message, severity: 'error' });
      setFilters({ ...filters, useLocation: false });
    }
  }, [locationError]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = useCallback(() => {
    setFilters({
      city: '',
      locality: '',
      includeNearby: true,
      minPrice: '',
      maxPrice: '',
      amenities: [],
      sortBy: 'name',
      useLocation: false,
      topRated: false,
      minDiscount: '',
      oneDayPass: false,
      nearbyRadius: 10,
    });
    setLocalities([]);
    setSearchQuery('');
    clearLocation();
    navigate(inStudentShell ? '/student/libraries' : '/libraries', { replace: true });
  }, [clearLocation, navigate, inStudentShell]);

  const handleSortChange = useCallback((e) => {
    setFilters((prev) => ({ ...prev, sortBy: e.target.value }));
  }, []);

  const handleAmenityToggle = useCallback((amenity) => {
    setFilters((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  }, []);

  const activeFiltersCount = [
    filters.city,
    filters.locality,
    filters.minPrice,
    filters.maxPrice,
    filters.useLocation,
    filters.amenities.length > 0,
    filters.topRated,
    filters.minDiscount,
    filters.oneDayPass,
  ].filter(Boolean).length;

  const moreFiltersCount = [
    filters.city,
    filters.locality,
    filters.minPrice,
    filters.maxPrice,
    filters.sortBy === 'distance' && !filters.useLocation,
    filters.amenities.some((a) => SHEET_AMENITIES.includes(a)),
  ].filter(Boolean).length;

  const FilterContent = ({ variant = 'sidebar' }) => {
    const isSheet = variant === 'sheet';
    const amenitiesSource = isSheet ? SHEET_AMENITIES : AMENITIES_LIST;
    const clearCount = isSheet ? moreFiltersCount : activeFiltersCount;

    return (
      <Box>
        {clearCount > 0 && (
          <Button size="small" onClick={clearAllFilters} sx={{ textTransform: 'none', mb: 2 }}>
            Clear All
          </Button>
        )}

        {!isSheet && (
          <>
            {/* Top Rated */}
            <Button
              fullWidth
              variant={filters.topRated ? 'contained' : 'outlined'}
              startIcon={<EmojiEvents />}
              onClick={() => setFilters((prev) => ({ ...prev, topRated: !prev.topRated }))}
              sx={{ mb: 2, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
            >
              {filters.topRated ? 'Showing Top Rated' : 'Top Rated Only'}
            </Button>

            {/* Near Me */}
            <Button
              fullWidth
              variant={filters.useLocation ? 'contained' : 'outlined'}
              startIcon={locationLoading ? <CircularProgress size={20} /> : <MyLocation />}
              onClick={handleNearMe}
              disabled={locationLoading}
              sx={{ mb: 2, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
            >
              {filters.useLocation ? 'Using Your Location' : 'Near Me'}
            </Button>

            {filters.useLocation && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Search Radius
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {[2, 5, 10, 15].map((km) => (
                    <Chip
                      key={km}
                      label={`${km} KM`}
                      onClick={() => setFilters((prev) => ({ ...prev, nearbyRadius: km }))}
                      color={filters.nearbyRadius === km ? 'primary' : 'default'}
                      variant={filters.nearbyRadius === km ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* One Day Pass */}
            <Button
              fullWidth
              variant={filters.oneDayPass ? 'contained' : 'outlined'}
              startIcon={<Today />}
              onClick={() => setFilters((prev) => ({ ...prev, oneDayPass: !prev.oneDayPass }))}
              sx={{ mb: 3, borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
            >
              {filters.oneDayPass ? 'Showing Day Pass' : 'Day Pass Available'}
            </Button>

            {/* Discount */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Minimum Discount</InputLabel>
              <Select
                value={filters.minDiscount}
                label="Minimum Discount"
                onChange={(e) => setFilters((prev) => ({ ...prev, minDiscount: e.target.value }))}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="10">10% or more</MenuItem>
                <MenuItem value="20">20% or more</MenuItem>
                <MenuItem value="30">30% or more</MenuItem>
                <MenuItem value="40">40% or more</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {/* City */}
        <Box sx={{ mb: 2 }}>
          <SearchableCityDropdown
            value={filters.city}
            onChange={(newCity) => setFilters((prev) => ({ ...prev, city: newCity || '' }))}
            detectedCity={detectedCity}
            includeNearby={filters.includeNearby}
            onIncludeNearbyChange={(checked) =>
              setFilters((prev) => ({ ...prev, includeNearby: checked }))
            }
            showIncludeNearbyCheckbox={true}
            label="City"
            placeholder="Search city..."
            fullWidth
          />
        </Box>

        {/* Area — dependent sub-filter; shown when a city is chosen */}
        <Box sx={{ mb: 3, pl: 0 }}>
          <SearchableAreaDropdown
            value={filters.locality}
            onChange={(newLocality) =>
              setFilters((prev) => ({ ...prev, locality: newLocality }))
            }
            localities={localities}
            loading={localitiesLoading}
            disabled={!filters.city}
            label="Area"
            placeholder="Search area..."
            fullWidth
          />
        </Box>

        {/* Sort By */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Sort By</InputLabel>
          <Select value={filters.sortBy} label="Sort By" onChange={handleSortChange}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="distance" disabled={!filters.useLocation}>
              Distance {!filters.useLocation && '(Enable Near Me)'}
            </MenuItem>
          </Select>
        </FormControl>

        {/* Amenities */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Amenities
        </Typography>
        <FormGroup>
          {amenitiesSource.map((amenity) => (
            <FormControlLabel
              key={amenity}
              control={
                <Checkbox
                  checked={filters.amenities.includes(amenity)}
                  onChange={() => handleAmenityToggle(amenity)}
                />
              }
              label={amenity}
            />
          ))}
        </FormGroup>
      </Box>
    );
  };

  // Shared search + filter controls — rendered in the body when inside student shell
  const SearchAndFilterControls = (
    <Box mb={2}>
      {/* Search bar */}
      <Box mb={1}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.subtle',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box display="flex" alignItems="center" px={2}>
            <Search sx={{ color: 'text.secondary', mr: 1 }} />
            <InputBase
              fullWidth
              placeholder="Search by name, city, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ py: 1.5, fontSize: '0.95rem' }}
            />
            {searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Quick-filter pill strip — mobile only */}
      {!isDesktop && (
        <LibraryFilterPills
          filters={filters}
          setFilters={setFilters}
          locationLoading={locationLoading}
          onNearMeClick={handleNearMe}
          onTurnOffNearMe={() => {
            setFilters((prev) => ({ ...prev, useLocation: false }));
            clearLocation();
          }}
          onOpenMore={() => setFilterOpen(true)}
          moreBadgeCount={moreFiltersCount}
          clearUrlParams={() => navigate(inStudentShell ? '/student/libraries' : '/libraries', { replace: true })}
        />
      )}

      {/* Active filter chips — mobile only, non-pill filters (city, area, price, sheet amenities) */}
      {moreFiltersCount > 0 && !isDesktop && (
        <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
          {filters.city && (
            <Chip
              label={`City: ${filters.city}`}
              onDelete={() => setFilters({ ...filters, city: '', locality: '' })}
              size="small"
            />
          )}
          {filters.locality && (
            <Chip
              label={`Area: ${localities.find((l) => l.value === filters.locality)?.label || filters.locality}`}
              onDelete={() => setFilters((prev) => ({ ...prev, locality: '' }))}
              size="small"
            />
          )}
          {filters.minPrice && (
            <Chip
              label={`Min: ₹${filters.minPrice}`}
              onDelete={() => setFilters({ ...filters, minPrice: '' })}
              size="small"
            />
          )}
          {filters.maxPrice && (
            <Chip
              label={`Max: ₹${filters.maxPrice}`}
              onDelete={() => setFilters({ ...filters, maxPrice: '' })}
              size="small"
            />
          )}
          {filters.amenities.filter((a) => SHEET_AMENITIES.includes(a)).map((amenity) => (
            <Chip
              key={amenity}
              label={amenity}
              onDelete={() =>
                setFilters({
                  ...filters,
                  amenities: filters.amenities.filter((a) => a !== amenity),
                })
              }
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
      {!inStudentShell && <DesktopNav />}

      {/* Sticky header — public flow only */}
      {!inStudentShell && (
        <Box
          sx={(theme) => ({
            ...auroraTopbarSx(theme),
            position: 'sticky',
            top: { xs: 0, md: 64 },
            zIndex: 100,
            borderBottom: 1,
            borderColor: 'divider',
            boxShadow: 1,
          })}
        >
          <Container maxWidth="xl" sx={{ py: 2 }}>
            <PageHeader
              title="Find Libraries"
              subtitle={`${libraries.length} ${libraries.length === 1 ? 'library' : 'libraries'} found`}
              backTo={handleBack}
              actions={null}
            />

            {/* Search bar */}
            <Box mt={1}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'background.subtle',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box display="flex" alignItems="center" px={2}>
                  <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  <InputBase
                    fullWidth
                    placeholder="Search by name, city, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ py: 1.5, fontSize: '0.95rem' }}
                  />
                  {searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Quick-filter pill strip — mobile only */}
            {!isDesktop && (
              <LibraryFilterPills
                filters={filters}
                setFilters={setFilters}
                locationLoading={locationLoading}
                onNearMeClick={handleNearMe}
                onTurnOffNearMe={() => {
                  setFilters((prev) => ({ ...prev, useLocation: false }));
                  clearLocation();
                }}
                onOpenMore={() => setFilterOpen(true)}
                moreBadgeCount={moreFiltersCount}
                clearUrlParams={() => navigate('/libraries', { replace: true })}
              />
            )}

            {/* Active filter chips — mobile only, non-pill filters (city, area, price, sheet amenities) */}
            {moreFiltersCount > 0 && !isDesktop && (
              <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                {filters.city && (
                  <Chip
                    label={`City: ${filters.city}`}
                    onDelete={() => setFilters({ ...filters, city: '', locality: '' })}
                    size="small"
                  />
                )}
                {filters.locality && (
                  <Chip
                    label={`Area: ${localities.find((l) => l.value === filters.locality)?.label || filters.locality}`}
                    onDelete={() => setFilters((prev) => ({ ...prev, locality: '' }))}
                    size="small"
                  />
                )}
                {filters.minPrice && (
                  <Chip
                    label={`Min: ₹${filters.minPrice}`}
                    onDelete={() => setFilters({ ...filters, minPrice: '' })}
                    size="small"
                  />
                )}
                {filters.maxPrice && (
                  <Chip
                    label={`Max: ₹${filters.maxPrice}`}
                    onDelete={() => setFilters({ ...filters, maxPrice: '' })}
                    size="small"
                  />
                )}
                {filters.amenities.filter((a) => SHEET_AMENITIES.includes(a)).map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onDelete={() =>
                      setFilters({
                        ...filters,
                        amenities: filters.amenities.filter((a) => a !== amenity),
                      })
                    }
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Main content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Search + filter controls in body when inside student shell */}
        {inStudentShell && SearchAndFilterControls}

        <Grid container spacing={3}>
          {/* Desktop sidebar */}
          {isDesktop && (
            <Grid item md={3}>
              <Paper
                sx={{
                  borderRadius: 3,
                  p: 3,
                  position: 'sticky',
                  top: 180,
                  maxHeight: 'calc(100vh - 200px)',
                  overflowY: 'auto',
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Filters
                </Typography>
                <FilterContent variant="sidebar" />
              </Paper>
            </Grid>
          )}

          {/* Libraries grid */}
          <Grid item xs={12} md={isDesktop ? 9 : 12}>
            <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.24 }}>
              {loading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : libraries.length === 0 ? (
                <EmptyState
                  icon={<EventSeat />}
                  title="No libraries found"
                  description="Try adjusting your search criteria or filters"
                  actionLabel="Clear All Filters"
                  onAction={clearAllFilters}
                />
              ) : (
                <>
                  <Grid container spacing={3}>
                    {libraries.map((library) => (
                      <Grid item xs={12} sm={6} md={isDesktop ? 4 : 6} key={library.id}>
                        <LibraryCard library={library} />
                      </Grid>
                    ))}
                  </Grid>

                  {pagination.hasNextPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => fetchLibraries(pagination.currentPage + 1, true)}
                        disabled={loading}
                        sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 600, textTransform: 'none', borderWidth: 2 }}
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading...
                          </>
                        ) : (
                          `Load More (${pagination.totalItems - libraries.length} remaining)`
                        )}
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {libraries.length} of {pagination.totalItems} libraries
                    </Typography>
                  </Box>
                </>
              )}
            </MotionBox>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile filter — BottomSheet on mobile, Dialog on desktop (desktop handled by sidebar) */}
      {!isDesktop && (
        <BottomSheet
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          title="Filters"
          maxHeight="90vh"
        >
          <FilterContent variant="sheet" />
          <Box sx={{ pt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setFilterOpen(false)}
              sx={{ borderRadius: 3, py: 1.5, fontWeight: 600, textTransform: 'none' }}
            >
              Show {libraries.length} {libraries.length === 1 ? 'Library' : 'Libraries'}
            </Button>
          </Box>
        </BottomSheet>
      )}

      {!inStudentShell && isAuthenticated && <StudentBottomNav />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LibrariesPage;
